import {
  buildStoredFromLocal,
  deriveLocalState,
  derivePlacementSummary,
  LocalState,
  Placement,
  PLACEMENTS,
  Side,
  SIDES,
  sideToKey,
  StripSlotKey,
} from '@cutr/constants/windows/strip-placement.ts';
import { ConfigurationValues } from '@cutr/constants/windows/windows';
import {
  BasicValue,
  CONFIGURATIONS_OPTIONS,
} from '@cutr/constants/windows/windows-configurations.ts';
import { useId, useMemo, useState } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import { Checkbox } from '@/primitives/Checkbox';
import { Label } from '@/windows/primitives/Label';
import { Radio } from '@/windows/primitives/Radio';
import { RadioGroup } from '@/windows/primitives/RadioGroup';

import { SubSubheader, unboxLabel } from '../Configurations';
import { useConfigurations } from '../ConfigurationsContext';
import { LocalGridSelectModal } from '../inputs/LocalGridSelectModal';
import { ConfigurationComponent } from '.';
import styles from './CoverStripsConfig.module.css';

type CoverStripsValue = NonNullable<ConfigurationValues['coverStrips']>;

const COVER_STRIPS_ALL_OPTIONS =
  CONFIGURATIONS_OPTIONS.coverStrips.insideTop.options;

const NONE_OPTION = COVER_STRIPS_ALL_OPTIONS.find((o) => o.value === 'none')!;

const CoverStripsInput = () => {
  const { t } = useTranslation();
  const placementLabelId = useId();
  const placementInputId = useId();
  const locationsLabelId = useId();
  const { configurations, dispatch } = useConfigurations();

  const [initial] = useState(() =>
    deriveLocalState(configurations.coverStrips, NONE_OPTION)
  );
  const [placement, setPlacement] = useState<Placement>(initial.placement);
  const [locations, setLocations] = useState<Set<Side>>(initial.locations);
  const [profile, setProfile] = useState<BasicValue>(initial.profile);
  const [customSlots, setCustomSlots] = useState<CoverStripsValue>(
    initial.customSlots
  );

  const profileOptions = useMemo(
    () =>
      COVER_STRIPS_ALL_OPTIONS.map((config) => ({
        config,
        imagePath:
          config.value === 'none'
            ? '/sorpetaler/none.jpg'
            : `/sorpetaler/coverStrips/${config.value}.png`,
      })),
    []
  );

  const dispatchFromLocal = (next: LocalState) => {
    dispatch({
      type: 'SET_COVER_STRIPS',
      value: buildStoredFromLocal(next, NONE_OPTION),
    });
  };

  const handlePlacementChange = (next: Placement) => {
    const nextCustomSlots =
      next === 'custom' && placement !== 'custom'
        ? buildStoredFromLocal(
            { placement, locations, profile, customSlots },
            NONE_OPTION
          )
        : customSlots;
    setPlacement(next);
    setCustomSlots(nextCustomSlots);
    dispatchFromLocal({
      placement: next,
      locations,
      profile,
      customSlots: nextCustomSlots,
    });
  };

  const handleLocationToggle = (side: Side, checked: boolean) => {
    const next = new Set(locations);
    if (checked) next.add(side);
    else next.delete(side);
    setLocations(next);
    dispatchFromLocal({ placement, locations: next, profile, customSlots });
  };

  const handleProfileChange = (next: BasicValue) => {
    setProfile(next);
    dispatchFromLocal({ placement, locations, profile: next, customSlots });
  };

  const handleCustomSlotChange = (
    key: keyof CoverStripsValue,
    value: BasicValue
  ) => {
    const next = { ...customSlots, [key]: value };
    setCustomSlots(next);
    dispatchFromLocal({ placement, locations, profile, customSlots: next });
  };

  const showSharedInputs =
    placement === 'interior' ||
    placement === 'exterior' ||
    placement === 'both';

  return (
    <>
      <Label id={placementLabelId} htmlFor={placementInputId}>
        {t('windows.configurations.coverStrips.placement.label')}
      </Label>
      <RadioGroup
        aria-labelledby={placementLabelId}
        id={placementInputId}
        orientation="horizontal"
        value={placement}
        style={{ minHeight: '2rem' }}
        onChange={(value) => handlePlacementChange(value as Placement)}
      >
        {PLACEMENTS.map((option) => (
          <Radio key={option} value={option}>
            {t(
              `windows.configurations.coverStrips.placement.options.${option}`
            )}
          </Radio>
        ))}
      </RadioGroup>

      {showSharedInputs && (
        <>
          <Label id={locationsLabelId}>
            {t('windows.configurations.coverStrips.locationOnFrame.label')}
          </Label>
          <div
            role="group"
            aria-labelledby={locationsLabelId}
            className={styles.locationsGroup}
          >
            {SIDES.map((side) => (
              <Checkbox
                key={side}
                checked={locations.has(side)}
                onChange={(e) => handleLocationToggle(side, e.target.checked)}
              >
                {t(
                  `windows.configurations.coverStrips.locationOnFrame.options.${side}`
                )}
              </Checkbox>
            ))}
          </div>
          <Label>{t('windows.configurations.coverStrips.profile.label')}</Label>
          <LocalGridSelectModal
            modalTitle={t(
              'windows.configurations.coverStrips.profile.modal.title'
            )}
            modalDescription={t(
              'windows.configurations.coverStrips.profile.modal.description'
            )}
            options={profileOptions}
            value={profile}
            onChange={handleProfileChange}
          />
        </>
      )}

      {placement === 'custom' &&
        (['inside', 'outside'] as const).map((face) => (
          <Fragment key={face}>
            <SubSubheader
              title={t(
                `windows.configurations.coverStrips.custom.${face}.label`
              )}
            />
            {SIDES.map((side) => {
              const key = sideToKey(face, side);
              return (
                <Fragment key={key}>
                  <Label>
                    {t(
                      `windows.configurations.coverStrips.custom.${face}.${side}`
                    )}
                  </Label>
                  <LocalGridSelectModal
                    modalTitle={t(
                      `windows.configurations.coverStrips.custom.${face}.${side}`
                    )}
                    modalDescription={t(
                      'windows.configurations.coverStrips.profile.modal.description'
                    )}
                    options={profileOptions}
                    value={customSlots[key]}
                    onChange={(value) => handleCustomSlotChange(key, value)}
                  />
                </Fragment>
              );
            })}
          </Fragment>
        ))}
    </>
  );
};

const CUSTOM_SLOT_LOCATION: Record<
  StripSlotKey,
  { face: 'inside' | 'outside'; side: Side }
> = {
  insideTop: { face: 'inside', side: 'top' },
  insideRight: { face: 'inside', side: 'right' },
  insideBottom: { face: 'inside', side: 'bottom' },
  insideLeft: { face: 'inside', side: 'left' },
  outsideTop: { face: 'outside', side: 'top' },
  outsideRight: { face: 'outside', side: 'right' },
  outsideBottom: { face: 'outside', side: 'bottom' },
  outsideLeft: { face: 'outside', side: 'left' },
};

const config: ConfigurationComponent<'coverStrips'> = {
  key: 'coverStrips',
  input: <CoverStripsInput />,
  display: (value: ConfigurationValues['coverStrips']) => {
    const summary = derivePlacementSummary(value);
    if (!summary) return '-';

    if (summary.placement === 'custom') {
      return (
        <div className={styles.display}>
          {summary.customEntries.map(([key, v]) => {
            const { face, side } = CUSTOM_SLOT_LOCATION[key];
            const faceLabel = i18n.t(
              `windows.configurations.coverStrips.custom.${face}.label`
            );
            const sideLabel = i18n.t(
              `windows.configurations.coverStrips.custom.${face}.${side}`
            );
            return (
              <div key={key}>
                <span>
                  {faceLabel} — {sideLabel}:{' '}
                </span>
                <span>{unboxLabel(v.label)}</span>
              </div>
            );
          })}
        </div>
      );
    }

    const placementLabel = i18n.t(
      `windows.configurations.coverStrips.placement.options.${summary.placement}`
    );
    const sidesLabel = summary.sides
      .map((side) =>
        i18n.t(
          `windows.configurations.coverStrips.locationOnFrame.options.${side}`
        )
      )
      .join(', ');

    return (
      <div className={styles.display}>
        <div>
          <span>
            {placementLabel}, {sidesLabel}:{' '}
          </span>
          <span>{unboxLabel(summary.profile.label)}</span>
        </div>
      </div>
    );
  },
};

export default config;
